import { ApiResponse } from "../../../../core/_models";
import { requestLaravel } from "../../../../../../utils/api";
import { IFinancialAndFundingStrategyEngine, IBaseCaseAndCounterfactualScenarioEngine, IDemandAndNeedsValidationEngine, IFeasibilityOption, IFiscalSustainabilityEngine, ICbaCeaIntelligenceEngine, IRiskAndSensitivityAnalysisEngine, ISummaryMatrix, IDecisionData, IEconomiesOfScaleAssessment, ISpecialProcurementActionsAndSocialImpact, IProjectFeasibilityAndOptionsAnalysis } from "./_models";

export function saveProjectStageTwo(payload: { project_id: number; dk_stage: string }, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<void>>(
        "/project-stage-two/stage/submit",
        { project_id: payload.project_id },
        { signal }
    );
}

/* =========================
   MINI EDITOR
========================= */

function updateprojectstage2minieditor<TResponse, TPayload extends { project_id: number }>(
    updateData: TPayload,
    signal?: AbortSignal
) {
    return requestLaravel<ApiResponse<TResponse>>(
        "/project-stage-two/mini-editor/update",
        updateData,
        { signal }
    );
}

function getprojectstage2minieditor<TResponse, TPayload extends { project_id: number }>(
    payload: TPayload,
    signal?: AbortSignal
) {
    return requestLaravel<ApiResponse<TResponse>>(
        "/project-stage-two/mini-editor/get",
        payload,
        { signal }
    );
}

export interface updateEconomiesOfScaleAssessmentPayload extends Omit<
    IEconomiesOfScaleAssessment,
    "formAction" | "iscompleted"
> {
    project_id: number;
}

export function updateEconomiesOfScaleAssessment(
    updateData: updateEconomiesOfScaleAssessmentPayload,
    signal?: AbortSignal
) {
    return updateprojectstage2minieditor<IEconomiesOfScaleAssessment, updateEconomiesOfScaleAssessmentPayload>(
        updateData,
        signal
    );
}

export function getEconomiesOfScaleAssessment(
    payload: updateEconomiesOfScaleAssessmentPayload,
    signal?: AbortSignal
) {
    return getprojectstage2minieditor<IEconomiesOfScaleAssessment, updateEconomiesOfScaleAssessmentPayload>(
        payload,
        signal
    );
}

export interface updateSpecialProcurementActionsAndSocialImpactPayload extends Omit<
    ISpecialProcurementActionsAndSocialImpact,
    "formAction" | "iscompleted"
> {
    project_id: number;
}

export function updateSpecialProcurementActionsAndSocialImpact(
    updateData: updateSpecialProcurementActionsAndSocialImpactPayload,
    signal?: AbortSignal
) {
    return updateprojectstage2minieditor<ISpecialProcurementActionsAndSocialImpact, updateSpecialProcurementActionsAndSocialImpactPayload>(
        updateData,
        signal
    );
}

export function getSpecialProcurementActionsAndSocialImpact(
    payload: updateSpecialProcurementActionsAndSocialImpactPayload,
    signal?: AbortSignal
) {
    return getprojectstage2minieditor<ISpecialProcurementActionsAndSocialImpact, updateSpecialProcurementActionsAndSocialImpactPayload>(
        payload,
        signal
    );
}

export function storeOrUpdateOptionsIdentification(payload: IFeasibilityOption & { project_id: number }, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IFeasibilityOption>>(
        "/project-stage-two/options-identification/save",
        payload,
        { signal }
    );
}

export function getOptionsIdentification(payload: { project_id: number }, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IFeasibilityOption[]>>(
        "/project-stage-two/options-identification/list",
        payload,
        { signal }
    );
}

export function deleteOptionsIdentification(payload: { project_id: number; row_id: number }, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<{ row_id: number }>>(
        "/project-stage-two/options-identification/delete",
        payload,
        { signal }
    );
}

export function updateDemandAndNeedsValidationEngine(
    updateData: IDemandAndNeedsValidationEngine & { project_id: number },
    signal?: AbortSignal
) {
    return requestLaravel<ApiResponse<IDemandAndNeedsValidationEngine>>(
        "/project-stage-two/demand-and-needs-validation/update",
        updateData,
        { signal }
    );
}

export function getBaseCaseAndCounterfactualScenario(project_id: number, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IBaseCaseAndCounterfactualScenarioEngine>>(
        "/project-stage-two/base-case-counterfactual-scenario/get",
        { project_id },
        { signal }
    );
}

export function getDemandAndNeedsValidationEngine(payload: { project_id: number }, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IDemandAndNeedsValidationEngine & { current_capacity: number }>>(
        "/project-stage-two/demand-and-needs-validation/get",
        payload,
        { signal }
    );
}

export function updateFiscalSustainability(
    updateData: IFiscalSustainabilityEngine & { project_id: number },
    signal?: AbortSignal
) {
    return requestLaravel<ApiResponse<IFiscalSustainabilityEngine>>(
        "/project-stage-two/fiscal-sustainability/update",
        updateData,
        { signal }
    );
}

interface UpdateBaseCaseAndCounterfactualScenarioPayload extends IBaseCaseAndCounterfactualScenarioEngine {
    project_id: number;
}

export function updateBaseCaseAndCounterfactualScenario(updateData: UpdateBaseCaseAndCounterfactualScenarioPayload) {
    return requestLaravel<ApiResponse<IBaseCaseAndCounterfactualScenarioEngine>>(
        "/project-stage-two/base-case-counterfactual-scenario/update",
        updateData
    );
}

export function getFinancialAndFundingStrategy(project_id: number, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IFinancialAndFundingStrategyEngine>>(
        "/project-stage-two/financial-funding-strategy/get",
        { project_id },
        { signal }
    );
}

interface UpdateFinancialAndFundingStrategyPayload extends IFinancialAndFundingStrategyEngine {
    project_id: number;
}

export function updateFinancialAndFundingStrategy(updateData: UpdateFinancialAndFundingStrategyPayload) {
    return requestLaravel<ApiResponse<IFinancialAndFundingStrategyEngine>>(
        "/project-stage-two/financial-funding-strategy/update",
        updateData
    );
}

export function getFiscalSustainability(payload: { project_id: number }, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IFiscalSustainabilityEngine>>(
        "/project-stage-two/fiscal-sustainability/get",
        payload,
        { signal }
    );
}

export function updateCbaCeaIntelligenceEngine(
    updateData: ICbaCeaIntelligenceEngine & { project_id: number },
    signal?: AbortSignal
) {
    return requestLaravel<ApiResponse<ICbaCeaIntelligenceEngine>>(
        "/project-stage-two/cba-cea-intelligence/update",
        updateData,
        { signal }
    );
}

export function getCbaCeaIntelligenceEngine(payload: { project_id: number }, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<ICbaCeaIntelligenceEngine>>(
        "/project-stage-two/cba-cea-intelligence/get",
        payload,
        { signal }
    );
}

export function storeOrUpdateSensitivityAnalysisEngine(payload: IRiskAndSensitivityAnalysisEngine & { project_id: number }) {
    return requestLaravel<ApiResponse<IRiskAndSensitivityAnalysisEngine>>(
        "/project-stage-two/sensitivity-analysis/update",
        payload
    );
}

export function getSensitivityAnalysis(payload: { project_id: number }, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IRiskAndSensitivityAnalysisEngine & { summary_matrix: ISummaryMatrix[] }>>(
        "/project-stage-two/sensitivity-analysis/get",
        payload,
        { signal }
    );
}

export function getStrategicResourcingDecisionEngine(payload: { project_id: number }, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IDecisionData | null>>(
        "/project-stage-two/strategic-resourcing-decision/get",
        payload,
        { signal }
    );
}



export function getJustificationForOptions(payload: { project_id: number }, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IProjectFeasibilityAndOptionsAnalysis>>(
        "/project-stage-two/justification-of-option/get",
        payload,
        { signal }
    );
}

export function StoreorUpdateJustificationForOptions(payload: IProjectFeasibilityAndOptionsAnalysis & { project_id: number }, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IProjectFeasibilityAndOptionsAnalysis>>(
        "/project-stage-two/justification-of-option/save",
        payload,
        { signal }
    );

}
